import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { permissionsApi } from '../../api/permissions';
import { authApi } from '../../api/auth';
import { rolesApi } from '../../api/roles';

const KEY = ['permission-matrix'] as const;
const ROLES_KEY = ['roles'] as const;

/** Admin only — the real, bounded 11-module permission matrix (built-in + custom roles). */
export function usePermissionMatrix() {
  return useQuery({ queryKey: KEY, queryFn: () => permissionsApi.get() });
}

/**
 * The signed-in user's own permissions (any staff role). The admin sidebar and page guard read this so a page disappears — and is refused —
 * as soon as an admin switches its access off in Permissions, instead of following a fixed role list. Refreshed every 30 s (the gateway's
 * own matrix cache is 30 s), so a change reaches an open session within about a minute.
 */
export function useMyPermissions(enabled = true) {
  return useQuery({
    queryKey: ['my-permissions'],
    queryFn: () => authApi.myPermissions(),
    enabled,
    staleTime: 20_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useUpdatePermissionCell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cell: { module: string; action: string; role: string; allowed: boolean }) => permissionsApi.update(cell),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Admin only — every assignable role, built-in and custom, WITH ids (so custom roles can be
 * deleted) — distinct from useUsers.ts's useRoles(), which hits the lighter GET
 * /api/v1/auth/roles (no ids, used just for the Users page's role picker). Both read the same
 * underlying built-in+custom role set; this one exists because role *management* (delete)
 * needs an id the other endpoint doesn't return. */
export function useRoleRegistry() {
  return useQuery({ queryKey: ROLES_KEY, queryFn: () => rolesApi.list() });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) => rolesApi.create(body),
    // Also invalidate the matrix (not just the roles list) — a new custom role needs its own
    // column to appear immediately so it can be granted permissions without a manual reload.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLES_KEY });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rolesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLES_KEY });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
